// src/contexts/AuthContext.jsx - Fixed version
import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  updateProfile,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider } from '../config/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    let mounted = true;
    
    const initializeAuth = async () => {
      try {
        // SET AUTH PERSISTENCE EXPLICITLY
        await setPersistence(auth, browserLocalPersistence);
        
        // Set up auth state listener
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          if (!mounted) return;

          if (firebaseUser) {
            setUser(firebaseUser);
            // Handle profile creation separately to avoid blocking auth state
            handleUserProfile(firebaseUser);
          } else {
            setUser(null);
            setUserProfile(null);
          }
          
          if (!authInitialized) {
            setAuthInitialized(true);
            setLoading(false);
          }
        });

        return unsubscribe;
      } catch (error) {
        console.error('Error initializing auth:', error);
        setLoading(false);
      }
    };

    const unsubscribePromise = initializeAuth();
    
    return () => {
      mounted = false;
      unsubscribePromise.then(unsubscribe => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, []);

  // Handle user profile creation/loading separately from auth state
  const handleUserProfile = async (firebaseUser) => {
    try {
      const userRef = doc(db, 'pb-users', firebaseUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const profileData = { id: userSnap.id, ...userSnap.data() };
        setUserProfile(profileData);
        
        // Update last sign-in time (non-blocking)
        setDoc(userRef, {
          lastSignInAt: serverTimestamp(),
          lastActiveAt: serverTimestamp()
        }, { merge: true }).catch(console.error);
        
      } else {
        // Create profile in background
        createUserProfile(firebaseUser).catch(console.error);
      }
    } catch (error) {
      console.error('Error handling user profile:', error);
    }
  };

  const createUserProfile = async (firebaseUser, additionalData = {}) => {
    try {
      const userRef = doc(db, 'pb-users', firebaseUser.uid);
      const { displayName, email, photoURL } = firebaseUser;

      const profileData = {
        uid: firebaseUser.uid,
        email,
        displayName: displayName || email?.split('@')[0] || 'User',
        photoURL: photoURL || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastSignInAt: serverTimestamp(),
        lastActiveAt: serverTimestamp(),
        role: 'user',
        plan: 'free',
        status: 'active',
        booksCreated: 0,
        totalViews: 0,
        totalInteractions: 0,
        ...additionalData
      };

      await setDoc(userRef, profileData);
      
      // Set profile in state
      const profileWithId = { id: firebaseUser.uid, ...profileData };
      setUserProfile(profileWithId);
      
      console.log('✅ User profile created successfully');
    } catch (error) {
      console.error('❌ Error creating user profile:', error);
    }
  };

  const signUp = async (email, password, displayName) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }
      
      return result;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  const signIn = async (email, password) => {
    try {
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Signin error:', error);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      return await signInWithPopup(auth, googleProvider);

    } catch (error) {
      console.error('Google signin error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const value = {
    user,
    userProfile,
    signUp,
    signIn,
    signInWithGoogle,
    logout,
    loading: loading || !authInitialized
  };

  return (
    <AuthContext.Provider value={value}>
      {(authInitialized || !loading) && children}
    </AuthContext.Provider>
  );
};